import { useState, useEffect } from "react";
import { useModelCatalog } from "./useModelCatalog";
import type {
  FlowConfigViewModel,
  PreprocessingOptions,
  StepRuntimeOptionsValue,
} from "@models/doctype";
import { generateStepsForFlowType } from "@utils/flowStepGenerator";
import type {
  FlowStepFormData,
  LLMStepConfigFormData,
  OCRStepConfigFormData,
  AgentExecutionFormData,
} from "@utils/builders/flowConfig";
import { buildFlowStepFormData } from "@utils/builders/flowConfig";

interface UseFlowWizardProps {
  open: boolean;
  existingConfig?: FlowConfigViewModel | null;
}

export const useFlowWizard = ({ open, existingConfig }: UseFlowWizardProps) => {
  const [activeStep, setActiveStep] = useState(0);
  const [activeTab, setActiveTab] = useState("0");
  const [flowType, setFlowType] = useState<string>("");
  const [flowDescription, setFlowDescription] = useState<string>("");
  const [preprocessingOptions, setPreprocessingOptions] =
    useState<PreprocessingOptions | null>(null);
  const [steps, setSteps] = useState<FlowStepFormData[]>([]);
  const [isFlowTypeSelected, setIsFlowTypeSelected] = useState(false);

  // Generate steps based on flow type. Advancing past the type selector is
  // driven by the Continue button (setIsFlowTypeSelected), not by selection.
  useEffect(() => {
    if (open && !existingConfig && flowType) {
      setSteps(generateStepsForFlowType(flowType));
    }
  }, [flowType, open, existingConfig]);

  useEffect(() => {
    if (existingConfig && open) {
      setFlowType(existingConfig.flowType);
      setFlowDescription(existingConfig.flowDescription || "");
      setPreprocessingOptions(existingConfig.preprocessingOptions || null);
      setIsFlowTypeSelected(true);
      setSteps(existingConfig.steps.map(buildFlowStepFormData));
    } else if (open) {
      setPreprocessingOptions(null);
    }
  }, [existingConfig, open]);

  // The current step's provider list (by its capability) and model suggestions.
  const currentStep = steps[activeStep];
  const currentConfig = currentStep?.llmStepConfig || currentStep?.ocrStepConfig;
  const selectedProvider = currentConfig?.provider ?? "";
  const catalog = useModelCatalog(currentStep?.ocrStepConfig ? "ocr" : "chat", open && isFlowTypeSelected);

  useEffect(() => {
    if (open && isFlowTypeSelected && selectedProvider) void catalog.loadModels(selectedProvider);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isFlowTypeSelected, selectedProvider]);

  const handleProviderChange = (providerKey: string) => {
    if (!providerKey || providerKey === selectedProvider) return;
    // A model name belongs to its provider: changing the provider clears it.
    updateCurrentStepConfig("provider", providerKey);
    updateCurrentStepConfig("modelName", "");
  };

  const handleModelChange = (modelName: string) => {
    updateCurrentStepConfig("modelName", modelName);
  };

  const updateAgents = (agents: AgentExecutionFormData[]) => {
    const updatedSteps = [...steps];
    const currentStep = updatedSteps[activeStep];
    if (currentStep.llmStepConfig) {
      currentStep.llmStepConfig = {
        ...currentStep.llmStepConfig,
        executions: agents,
      };
      setSteps(updatedSteps);
    }
  };

  const updateCurrentStepConfig = (
    field: string,
    value: string | object | null,
  ) => {
    const updatedSteps = [...steps];
    const currentStep = updatedSteps[activeStep];

    if (currentStep.llmStepConfig) {
      currentStep.llmStepConfig = {
        ...currentStep.llmStepConfig,
        [field]: value,
      } as LLMStepConfigFormData;
    } else if (currentStep.ocrStepConfig) {
      currentStep.ocrStepConfig = {
        ...currentStep.ocrStepConfig,
        [field]: value,
      } as OCRStepConfigFormData;
    }

    setSteps(updatedSteps);
  };

  const updateCurrentStepRuntimeOptions = (
    value: StepRuntimeOptionsValue | null,
  ) => {
    const updatedSteps = [...steps];
    const currentStep = updatedSteps[activeStep];
    if (!currentStep.llmStepConfig) return;
    const runtime = value || {};
    currentStep.llmStepConfig = {
      ...currentStep.llmStepConfig,
      fallbackModel: runtime.fallbackModel,
      chunkingOptions: runtime.chunkingOptions,
      pageDetectionPolicy: runtime.pageDetectionPolicy,
    };
    setSteps(updatedSteps);
  };

  const isStepValid = (stepIndex: number): boolean => {
    const step = steps[stepIndex];
    if (!step) return false;

    if (step.llmStepConfig) {
      const config = step.llmStepConfig;
      const agents = config.executions ?? [];
      const hasBase = !!(config.provider && config.modelName && config.systemPrompt);
      if (!hasBase) return false;

      // Multi-agent: each agent carries its own prompt and schema, the step's are unused.
      if (agents.length) {
        const keys = agents.map((a) => a.domainKey.trim());
        return new Set(keys).size === keys.length && agents.every(
          (a) => a.domainKey.trim() && a.userPrompt.trim() && a.responseSchema,
        );
      }
      return !!config.userPrompt.trim();
    }

    if (step.ocrStepConfig) {
      return !!(step.ocrStepConfig.provider && step.ocrStepConfig.modelName);
    }

    return false;
  };

  const canProceed = (): boolean => {
    return isStepValid(activeStep);
  };

  const canSubmit = (): boolean => {
    return steps.every((_, index) => isStepValid(index));
  };

  const handleNext = () => {
    if (activeStep < steps.length - 1) {
      setActiveStep(activeStep + 1);
      setActiveTab("0");
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
      setActiveTab("0");
    }
  };

  return {
    activeStep,
    activeTab,
    flowType,
    flowDescription,
    preprocessingOptions,
    steps,
    isFlowTypeSelected,
    providers: catalog.providers,
    models: catalog.modelsFor(selectedProvider),
    selectedProvider,
    isLoadingModels: catalog.isLoadingModels,
    isLoadingProviders: catalog.isLoadingProviders,
    setActiveTab,
    setFlowType,
    setFlowDescription,
    setPreprocessingOptions,
    setIsFlowTypeSelected,
    handleProviderChange,
    handleModelChange,
    updateCurrentStepConfig,
    updateCurrentStepRuntimeOptions,
    isStepValid,
    canProceed,
    canSubmit,
    handleNext,
    handleBack,
    updateAgents,
  };
};
